interface ReviewRatings {
  idea: number;
  product: number;
  team: number;
  marketing: number;
  overall: number;
}

interface ReviewComments {
  idea: string;
  product: string;
  team: string;
  marketing: string;
  overall: string;
}

interface Review {
  id: number;
  dealName: string;
  reviewer: string;
  date: string;
  ratings: ReviewRatings;
  comments: ReviewComments;
}

export const TeamReviewsComponent = () => {
  const reviews: Review[] = [
    {
      id: 1,
      dealName: "Series A Investment",
      reviewer: "John Smith",
      date: "2023-07-20",
      ratings: {
        idea: 4,
        product: 5,
        team: 4,
        marketing: 3,
        overall: 4,
      },
      comments: {
        idea: "Strong problem-solution fit with clear market opportunity",
        product: "Innovative features with good scalability potential",
        team: "Experienced leadership with proven track record",
        marketing: "GTM strategy needs refinement",
        overall: "Strong team and product market fit",
      },
    },
    {
      id: 2,
      dealName: "Seed Round",
      reviewer: "Sarah Johnson",
      date: "2023-07-19",
      ratings: {
        idea: 3,
        product: 3,
        team: 4,
        marketing: 3,
        overall: 3,
      },
      comments: {
        idea: "Interesting concept but needs market validation",
        product: "Product features need more differentiation",
        team: "Strong technical background",
        marketing: "Customer acquisition strategy unclear",
        overall: "Need more information about financials",
      },
    },
  ]; // TODO: Replace with actual API call

  // Group reviews by deal name
  const dealReviews = reviews.reduce((acc, review) => {
    if (!acc[review.dealName]) {
      acc[review.dealName] = {
        reviews: [],
        averageRating: 0
      };
    }
    acc[review.dealName].reviews.push(review);
    return acc;
  }, {} as Record<string, { reviews: Review[], averageRating: number }>);

  // Calculate average ratings
  Object.values(dealReviews).forEach(deal => {
    deal.averageRating = deal.reviews.reduce((sum, review) => sum + review.ratings.overall, 0) / deal.reviews.length;
  });

  return (
    <div className="h-dvh flex flex-col lg:p-4">
      <div className="flex justify-between items-center mb-6 px-4 lg:px-0">
        <h1 className="text-2xl font-bold">Team Reviews</h1>
      </div>

      <div className="flex-1 bg-white lg:rounded-lg lg:shadow overflow-auto">
        {/* Desktop view */}
        <div className="hidden lg:block">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4">Deal Name</th>
                <th className="text-left p-4">Overall Rating</th>
                <th className="text-left p-4">Number of Reviews</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(dealReviews).map(([dealName, data]) => (
                <tr key={dealName} className="border-b hover:bg-gray-50">
                  <td className="p-4">{dealName}</td>
                  <td className="p-4">
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`${
                            star <= data.averageRating
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4">{data.reviews.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile view */}
        <div className="lg:hidden">
          {Object.entries(dealReviews).map(([dealName, data]) => (
            <div key={dealName} className="border-b p-4">
              <div className="flex flex-col space-y-2">
                <h3 className="font-medium text-lg">{dealName}</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">Overall:</span>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`${
                          star <= data.averageRating
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  Number of Reviews: {data.reviews.length}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamReviewsComponent;
